<template>
  <div class="ocr-editable-grid">
    <div v-if="!localColumns.length" class="ocr-empty-cell">No table data to edit.</div>
    <template v-else>
      <div class="ocr-editable-grid-toolbar">
        <v-btn size="small" variant="text" class="ocr-toolbar-btn" :disabled="!canUndo" @click="undo">Undo</v-btn>
        <v-btn size="small" variant="text" class="ocr-toolbar-btn" :disabled="!canRedo" @click="redo">Redo</v-btn>
        <v-menu>
          <template #activator="{ props }">
            <v-btn v-bind="props" size="small" variant="outlined" class="ocr-toolbar-btn" append-icon="mdi-chevron-down">
              Row functions
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item title="Select all rows" :disabled="!localData.length" @click="selectAllRows" />
            <v-list-item title="Clear row selection" :disabled="!selectedRows.length" @click="clearRows" />
            <v-list-item title="Add row" @click="addRow" />
            <v-list-item title="Duplicate row" :disabled="selectedRows.length !== 1" @click="duplicateRow" />
            <v-list-item :title="`Delete row(s)${selectedRows.length ? ` (${selectedRows.length})` : ''}`" :disabled="!selectedRows.length" @click="deleteRows" />
            <v-list-item title="Move row(s) up" :disabled="!selectedRows.length" @click="moveRows(-1)" />
            <v-list-item title="Move row(s) down" :disabled="!selectedRows.length" @click="moveRows(1)" />
            <v-list-item title="Split table" :disabled="!selectedRows.length" @click="$emit('split', selectedRows)" />
            <v-list-item title="Duplicate table" @click="$emit('duplicate-table')" />
          </v-list>
        </v-menu>
        <v-checkbox
          class="ocr-select-all-columns"
          :model-value="allColumnsSelected"
          :indeterminate="someColumnsSelected && !allColumnsSelected"
          hide-details
          density="compact"
          label="All columns"
          aria-label="Select all columns"
          @update:model-value="toggleAllColumns"
        />
        <v-menu>
          <template #activator="{ props }">
            <v-btn v-bind="props" size="small" variant="outlined" class="ocr-toolbar-btn" append-icon="mdi-chevron-down">
              Column functions
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item title="Select all columns" :disabled="!localColumns.length" @click="selectAllColumns" />
            <v-list-item title="Clear column selection" :disabled="!selectedCols.length" @click="clearColumns" />
            <v-list-item title="Add column" @click="addColumn" />
            <v-list-item title="Duplicate column" :disabled="columnSelection.length !== 1" @click="duplicateColumn" />
            <v-list-item title="Delete column" :disabled="localColumns.length < 2 || !columnSelection.length" @click="deleteColumn" />
            <v-list-item title="Merge columns" :disabled="selectedCols.length < 2" @click="mergeColumns" />
          </v-list>
        </v-menu>
        <v-switch
          :model-value="ignored"
          density="compact"
          hide-details
          color="primary"
          label="Ignore table"
          class="ocr-ignore-switch"
          @update:model-value="$emit('update:ignored', $event)"
        />
        <v-spacer />
        <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="saving" @click="emitSave">
          Save edits
        </v-btn>
      </div>
      <OcrTableWrap>
        <v-table class="well-table sample-table well-table-data fd-table">
          <thead>
            <tr class="main-header">
              <th class="ocr-select-col">
                <v-checkbox
                  :model-value="allRowsSelected"
                  :indeterminate="someRowsSelected && !allRowsSelected"
                  hide-details
                  density="compact"
                  aria-label="Select all rows"
                  @update:model-value="toggleAllRows"
                />
              </th>
              <th
                v-for="(col, cIdx) in localColumns"
                :key="col + cIdx"
                class="ocr-editable-col-head"
                :class="{ 'ocr-editable-col-head--active': selectedCols.includes(cIdx) }"
                @dragover.prevent
                @drop="onColDrop(cIdx)"
              >
                <v-checkbox
                  :model-value="selectedCols.includes(cIdx)"
                  hide-details
                  density="compact"
                  :aria-label="`Select column ${cIdx + 1}`"
                  @click.stop
                  @update:model-value="setCol(cIdx, $event)"
                />
                <span
                  class="ocr-drag-handle"
                  draggable="true"
                  title="Drag to reorder"
                  @click.stop
                  @dragstart="onColDragStart(cIdx, $event)"
                >⋮⋮</span>
                <input
                  v-model="localColumns[cIdx]"
                  class="ocr-editable-grid-input ocr-editable-col-input"
                  type="text"
                  :aria-label="`Column ${cIdx + 1} name`"
                  @click.stop
                  @focus="onCellFocus"
                  @input="onCellInput"
                  @blur="onCellBlur"
                />
              </th>
            </tr>
          </thead>
          <tbody class="well-table-content">
            <tr
              v-for="(row, rIdx) in localData"
              :key="rIdx"
              :class="{ 'ocr-table-pick-row--active': selectedRows.includes(rIdx) }"
              @dragover.prevent
              @drop="onRowDrop(rIdx)"
            >
              <td class="ocr-select-col">
                <span
                  class="ocr-drag-handle"
                  draggable="true"
                  title="Drag to reorder"
                  @dragstart="onRowDragStart(rIdx, $event)"
                >⋮⋮</span>
                <v-checkbox
                  :model-value="selectedRows.includes(rIdx)"
                  hide-details
                  density="compact"
                  @update:model-value="toggleRow(rIdx)"
                />
              </td>
              <td
                v-for="(col, cIdx) in localColumns"
                :key="col + rIdx"
                :class="{ 'ocr-editable-col--active': selectedCols.includes(cIdx) }"
              >
                <input
                  v-model="localData[rIdx][cIdx]"
                  class="ocr-editable-grid-input"
                  type="text"
                  @focus="onCellFocus"
                  @input="onCellInput"
                  @blur="onCellBlur"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
      </OcrTableWrap>
    </template>
  </div>
</template>

<script>
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrEditableGrid",
  components: { OcrTableWrap },
  props: {
    grid: { type: Object, default: null },
    saving: { type: Boolean, default: false },
    ignored: { type: Boolean, default: false },
  },
  emits: ["save", "reset", "split", "duplicate-table", "update:ignored", "selection"],
  data() {
    return {
      localColumns: [],
      localData: [],
      selectedRows: [],
      selectedCol: null,
      selectedCols: [],
      dragRow: null,
      dragCol: null,
      undoStack: [],
      redoStack: [],
      pendingHistory: null,
    };
  },
  computed: {
    canUndo() {
      return this.undoStack.length > 0;
    },
    canRedo() {
      return this.redoStack.length > 0;
    },
    columnSelection() {
      if (this.selectedCols.length) return this.selectedCols;
      return this.selectedCol == null ? [] : [this.selectedCol];
    },
    allColumnsSelected() {
      return this.localColumns.length > 0 && this.selectedCols.length === this.localColumns.length;
    },
    someColumnsSelected() {
      return this.selectedCols.length > 0;
    },
    allRowsSelected() {
      return this.localData.length > 0 && this.selectedRows.length === this.localData.length;
    },
    someRowsSelected() {
      return this.selectedRows.length > 0;
    },
  },
  watch: {
    grid: {
      immediate: true,
      deep: true,
      handler() {
        this.syncFromGrid();
      },
    },
  },
  methods: {
    syncFromGrid() {
      const g = this.grid;
      if (!g || !g.columns || !g.columns.length) {
        this.localColumns = [];
        this.localData = [];
        this.selectedRows = [];
        this.selectedCol = null;
        return;
      }
      this.localColumns = [...g.columns];
      this.localData = (g.data || []).map((row) =>
        this.localColumns.map((_, i) => (row[i] != null ? String(row[i]) : "")),
      );
      this.selectedRows = [];
      this.selectedCols = [];
      this.selectedCol = null;
      this.undoStack = [];
      this.redoStack = [];
      this.pendingHistory = null;
    },
    snapshot() {
      return {
        columns: [...this.localColumns],
        data: this.localData.map((row) => [...row]),
        selectedRows: [...this.selectedRows],
        selectedCol: this.selectedCol,
        selectedCols: [...this.selectedCols],
      };
    },
    applySnapshot(snap) {
      this.localColumns = [...snap.columns];
      this.localData = snap.data.map((row) => [...row]);
      this.selectedRows = [...(snap.selectedRows || [])];
      this.selectedCol = snap.selectedCol ?? null;
      this.selectedCols = [...(snap.selectedCols || [])];
      this.pendingHistory = null;
    },
    pushHistory() {
      this.undoStack.push(this.pendingHistory || this.snapshot());
      if (this.undoStack.length > 50) this.undoStack.shift();
      this.redoStack = [];
      this.pendingHistory = null;
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(this.snapshot());
      this.applySnapshot(this.undoStack.pop());
    },
    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(this.snapshot());
      this.applySnapshot(this.redoStack.pop());
    },
    onCellFocus() {
      if (!this.pendingHistory) this.pendingHistory = this.snapshot();
    },
    onCellInput() {
      if (!this.pendingHistory) return;
      this.pushHistory();
    },
    onCellBlur() {
      this.pendingHistory = null;
    },
    uniqueColumnName(base = "Column") {
      const existing = new Set(this.localColumns.map((c) => String(c)));
      let i = this.localColumns.length + 1;
      let name = `${base} ${i}`;
      while (existing.has(name)) {
        i += 1;
        name = `${base} ${i}`;
      }
      return name;
    },
    addRow() {
      this.pushHistory();
      const insertAt = this.selectedRows.length
        ? Math.max(...this.selectedRows) + 1
        : this.localData.length;
      const blank = this.localColumns.map(() => "");
      this.localData.splice(insertAt, 0, blank);
      this.selectedRows = [insertAt];
    },
    deleteRows() {
      if (!this.selectedRows.length) return;
      this.pushHistory();
      const drop = new Set(this.selectedRows);
      this.localData = this.localData.filter((_, i) => !drop.has(i));
      this.selectedRows = [];
      this.emitSelection();
    },
    moveRows(direction) {
      if (!this.selectedRows.length) return;
      const indexes = [...this.selectedRows].sort((a, b) => a - b);
      if (direction < 0 && indexes[0] === 0) return;
      if (direction > 0 && indexes[indexes.length - 1] === this.localData.length - 1) return;
      this.pushHistory();
      const order = direction < 0 ? indexes : [...indexes].reverse();
      order.forEach((index) => {
        const target = index + direction;
        const [row] = this.localData.splice(index, 1);
        this.localData.splice(target, 0, row);
      });
      this.selectedRows = indexes.map((index) => index + direction);
      this.emitSelection();
    },
    addColumn() {
      this.pushHistory();
      const name = this.uniqueColumnName();
      const at = this.selectedCol == null ? this.localColumns.length : this.selectedCol + 1;
      this.localColumns.splice(at, 0, name);
      this.localData = this.localData.map((row) => {
        const next = [...row];
        next.splice(at, 0, "");
        return next;
      });
      this.selectedCol = at;
    },
    deleteColumn() {
      const indexes = this.columnSelection.filter((i) => i >= 0 && i < this.localColumns.length);
      if (!indexes.length || indexes.length >= this.localColumns.length) return;
      this.pushHistory();
      const drop = new Set(indexes);
      this.localColumns = this.localColumns.filter((_, i) => !drop.has(i));
      this.localData = this.localData.map((row) => row.filter((_, i) => !drop.has(i)));
      this.selectedCols = [];
      this.selectedCol = null;
    },
    duplicateRow() {
      if (this.selectedRows.length !== 1) return;
      const idx = this.selectedRows[0];
      this.pushHistory();
      this.localData.splice(idx + 1, 0, [...this.localData[idx]]);
      this.selectedRows = [idx + 1];
    },
    duplicateColumn() {
      if (this.selectedCol == null) return;
      this.pushHistory();
      const at = this.selectedCol;
      const name = this.uniqueColumnName(this.localColumns[at] || "Column");
      this.localColumns.splice(at + 1, 0, name);
      this.localData = this.localData.map((row) => {
        const next = [...row];
        next.splice(at + 1, 0, row[at] ?? "");
        return next;
      });
      this.selectedCol = at + 1;
      this.selectedCols = [at + 1];
    },
    mergeColumns() {
      const cols = [...this.selectedCols].sort((a, b) => a - b);
      if (cols.length < 2) return;
      this.pushHistory();
      const keep = cols[0];
      const drop = new Set(cols.slice(1));
      this.localData = this.localData.map((row) => {
        const merged = cols.map((i) => String(row[i] ?? "").trim()).filter(Boolean).join(" ");
        return row.map((cell, i) => (i === keep ? merged : cell)).filter((_, i) => !drop.has(i));
      });
      this.localColumns = this.localColumns.filter((_, i) => !drop.has(i));
      this.selectedCol = keep;
      this.selectedCols = [keep];
    },
    toggleCol(idx) {
      const set = new Set(this.selectedCols);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      this.selectedCols = [...set].sort((a, b) => a - b);
      this.selectedCol = idx;
    },
    onRowDragStart(idx, event) {
      this.dragRow = idx;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(idx));
    },
    onRowDrop(idx) {
      if (this.dragRow == null || this.dragRow === idx) {
        this.dragRow = null;
        return;
      }
      this.pushHistory();
      const [row] = this.localData.splice(this.dragRow, 1);
      this.localData.splice(idx, 0, row);
      this.selectedRows = [idx];
      this.dragRow = null;
    },
    onColDragStart(idx, event) {
      this.dragCol = idx;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", `col-${idx}`);
    },
    onColDrop(idx) {
      const from = this.dragCol;
      this.dragCol = null;
      if (from == null || from === idx) return;
      this.pushHistory();
      const [name] = this.localColumns.splice(from, 1);
      this.localColumns.splice(idx, 0, name);
      this.localData = this.localData.map((row) => {
        const next = [...row];
        const [cell] = next.splice(from, 1);
        next.splice(idx, 0, cell);
        return next;
      });
      this.selectedCol = idx;
      this.selectedCols = [idx];
    },
    toggleRow(idx) {
      const set = new Set(this.selectedRows);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      this.selectedRows = [...set].sort((a, b) => a - b);
      this.emitSelection();
    },
    selectAllRows() {
      this.selectedRows = this.localData.map((_, idx) => idx);
      this.emitSelection();
    },
    clearRows() {
      this.selectedRows = [];
      this.emitSelection();
    },
    toggleAllRows(on) {
      if (on) this.selectAllRows();
      else this.clearRows();
    },
    selectAllColumns() {
      this.selectedCols = this.localColumns.map((_, idx) => idx);
      this.selectedCol = this.selectedCols[0] ?? null;
      this.emitSelection();
    },
    clearColumns() {
      this.selectedCols = [];
      this.selectedCol = null;
      this.emitSelection();
    },
    toggleAllColumns(on) {
      if (on) this.selectAllColumns();
      else this.clearColumns();
    },
    setCol(idx, on) {
      const set = new Set(this.selectedCols);
      if (on) set.add(idx);
      else set.delete(idx);
      this.selectedCols = [...set].sort((a, b) => a - b);
      this.selectedCol = idx;
      this.emitSelection();
    },
    currentGrid() {
      return {
        columns: [...this.localColumns],
        data: this.localData.map((row) => this.localColumns.map((_, i) => (row[i] != null ? row[i] : ""))),
      };
    },
    emitSelection() {
      this.$emit("selection", {
        rows: [...this.selectedRows],
        columns: this.selectedCols.map((idx) => this.localColumns[idx]).filter((name) => name != null),
      });
    },
    emitSave() {
      const grid = {
        columns: [...this.localColumns],
        data: this.localData.map((row) => this.localColumns.map((_, i) => (row[i] != null ? row[i] : ""))),
      };
      this.$emit("save", grid);
    },
  },
};
</script>
